import { Brain } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Textarea } from 'components/ui/textarea';
import { useQuerySSE } from 'features/chat/hooks/use-agents';
import type { ISSEQueryAgentPayload } from 'features/chat/agent-chat-module/types/agent.service.type';
import { AnimatedDots } from 'features/chat/agent-chat-module/utils/chat-event-messages';
import { parseSSEBuffer } from 'features/chat/agent-chat-module/utils/parse-sse';
import { getApiErrorDetail } from 'utils/api-error';
import { buildGreetingGenerationPrompt } from './greeting-generation-prompt';

type GreetingAiModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUse: (greeting: string) => void;
  agentName?: string;
  greetingValue: string;
};

export function GreetingAiModal({
  open,
  onOpenChange,
  onUse,
  agentName,
  greetingValue,
}: GreetingAiModalProps) {
  const { t } = useTranslation();
  const { streamQuery } = useQuerySSE();
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedGreeting, setGeneratedGreeting] = useState('');

  const handleClose = useCallback(() => {
    onOpenChange(false);
    setGeneratedGreeting('');
    setIsGenerating(false);
  }, [onOpenChange]);

  const generateGreeting = useCallback(async () => {
    try {
      setIsGenerating(true);
      setGeneratedGreeting('');

      const resolvedAgentName = agentName || t('ASSISTANT');
      const payload: ISSEQueryAgentPayload = {
        query:
          greetingValue?.trim() ||
          t('DEFAULT_GREETING_QUERY', { name: resolvedAgentName }),
        base_prompt: buildGreetingGenerationPrompt(resolvedAgentName),
        last_n_turns: 5,
        enable_summary: false,
        enable_tools: false,
        enable_next_suggestion: false,
        call_from: 'blocks',
      };

      const reader = await streamQuery(payload);
      const decoder = new TextDecoder();
      let buffer = '';
      let isDone = false;

      while (!isDone) {
        const { done, value } = await reader.read();
        isDone = done;
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const { events, remaining } = parseSSEBuffer(buffer);
        buffer = remaining;

        events.forEach(({ eventType, eventData }) => {
          switch (eventType) {
            case 'chat_response':
            case 'final_answer': {
              const content = (eventData.result || eventData.message) as string;
              if (content) {
                setGeneratedGreeting(content);
                setIsGenerating(false);
              }
              break;
            }
            case 'error':
              setGeneratedGreeting(t('GREETING_GENERATION_ERROR'));
              setIsGenerating(false);
              break;
            default:
              break;
          }
        });
      }

      setIsGenerating(false);
    } catch (error) {
      setGeneratedGreeting(getApiErrorDetail(error) ?? t('FAILED_TO_GENERATE_GREETING'));
      setIsGenerating(false);
    }
  }, [agentName, greetingValue, streamQuery, t]);

  useEffect(() => {
    if (open && generatedGreeting === '' && !isGenerating) {
      void generateGreeting();
    }
  }, [generateGreeting, generatedGreeting, isGenerating, open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          handleClose();
        }
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">{t('WELCOME_GUIDE')}</DialogTitle>
        </DialogHeader>

        <div className="pb-4">
          <label className="mb-3 block text-base font-medium">{t('GREETING')}</label>

          {isGenerating ? (
            <div className="flex min-h-[120px] items-center justify-center rounded-lg border bg-card">
              <div className="flex flex-col items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary">
                  <Brain className="h-6 w-6 animate-pulse text-white dark:text-foreground" />
                </div>
                <div className="flex flex-row items-center gap-2">
                  <span className="text-sm text-muted-foreground">{t('GENERATING')}</span>
                  <AnimatedDots />
                </div>
              </div>
            </div>
          ) : (
            <div className="relative">
              <Textarea
                value={generatedGreeting}
                onChange={(event) => setGeneratedGreeting(event.target.value)}
                placeholder={t('TYPE_YOUR_AGENT_GREETING')}
                rows={4}
                className="resize-none border bg-card"
              />
              <div className="mt-2 text-right text-sm text-muted-foreground">
                {t('GREETING_CHARACTER_COUNT', { value: generatedGreeting.length })}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => void generateGreeting()}
            disabled={isGenerating}
            className="mr-auto pl-0 text-primary hover:bg-transparent"
          >
            {t('REGENERATE')}
          </Button>
          <Button type="button" variant="outline" onClick={handleClose}>
            {t('CANCEL')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              onUse(generatedGreeting);
              handleClose();
            }}
            disabled={isGenerating || !generatedGreeting.trim()}
          >
            {t('USE')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
